/**
 * BytePlus ModelArk client for Studio generation.
 * Chat/tools via OpenAI-compatible AI SDK; images + video via Ark REST.
 */
import { seedanceTaskTypeConstraints } from "./seedanceTaskType";
import { createOpenAI, type OpenAIProvider } from "@ai-sdk/openai";
import { arkEnabled, assertArkEnabled } from "./mediaProvider";
import "./aiUsage"; // AI usage meter (logs every paid model call)

export const ARK_DEFAULT_BASE_URL =
  "https://ark.ap-southeast.bytepluses.com/api/v3";

export const ARK_MODEL_IDS = {
  image: "dola-seedream-5-0-pro-260628",
  imageFlash: "dola-seedream-5-0-flash-260915",
  imageLite: "seedream-5-0-lite-260128",
  image45: "seedream-4-5-251128",
  image40: "seedream-4-0-250828",
  video25: "dreamina-seedance-2-5-260628",
  video20: "dreamina-seedance-2-0-260128",
  video20Fast: "dreamina-seedance-2-0-fast-260128",
  video20Mini: "dreamina-seedance-2-0-mini-260615",
  /** Agent / Assistance / DM Improve / enhance — Seed 2.1 Turbo (multimodal). */
  text: "dola-seed-2-1-turbo-260628",
  /** Previous flagship — Assistance fallback / optional Pro tier. */
  textPro: "seed-2-0-pro-260328",
  textLite: "seed-2-0-lite-260428",
  textMini: "seed-2-0-mini-260428",
} as const;

const SEEDREAM_MODEL_IDS = new Set<string>([
  ARK_MODEL_IDS.image,
  ARK_MODEL_IDS.imageFlash,
  ARK_MODEL_IDS.imageLite,
  ARK_MODEL_IDS.image45,
  ARK_MODEL_IDS.image40,
  "seedream-5-0-260128",
]);

const LEGACY_MODEL_MAP: Record<string, string> = {
  "bytedance/seedance-2.5": ARK_MODEL_IDS.video25,
  "bytedance/seedance-2.0": ARK_MODEL_IDS.video20,
  "google/gemini-3.5-flash": ARK_MODEL_IDS.text,
  "google/gemini-2.5-flash-lite": ARK_MODEL_IDS.text,
  "google/gemini-2.5-flash": ARK_MODEL_IDS.text,
  "seed-2-0-pro-260328": ARK_MODEL_IDS.textPro,
  "seed-2-0-lite-260428": ARK_MODEL_IDS.textLite,
};

let cachedProvider: OpenAIProvider | null = null;

export function arkApiKey(): string {
  const key = process.env.ARK_API_KEY?.trim();
  if (!key) {
    throw new Error("ARK_API_KEY is not configured");
  }
  return key;
}

export function arkBaseUrl(): string {
  const base =
    process.env.ARK_BASE_URL?.trim() || ARK_DEFAULT_BASE_URL;
  return base.replace(/\/+$/, "");
}

export function resolveArkModelId(modelId: string | undefined | null): string {
  const raw = modelId?.trim();
  if (!raw) {
    throw new Error("Model id is required");
  }
  if (LEGACY_MODEL_MAP[raw]) return LEGACY_MODEL_MAP[raw];
  // Vercel-style prefixes → Ark ids when slug-like
  if (raw === "seedance-2.5" || raw.includes("seedance-2.5") || raw.includes("seedance-2-5")) {
    return ARK_MODEL_IDS.video25;
  }
  if (raw.includes("seedance-2.0-fast") || raw.includes("seedance-2-0-fast")) {
    return ARK_MODEL_IDS.video20Fast;
  }
  if (raw.includes("seedance-2.0-mini") || raw.includes("seedance-2-0-mini")) {
    return ARK_MODEL_IDS.video20Mini;
  }
  if (
    raw === "seedance-2.0" ||
    raw.includes("seedance-2.0") ||
    (raw.includes("seedance-2-0") && !raw.includes("2-5"))
  ) {
    return ARK_MODEL_IDS.video20;
  }
  if (SEEDREAM_MODEL_IDS.has(raw)) return raw;
  if (raw.includes("seedream")) {
    return ARK_MODEL_IDS.image;
  }
  return raw;
}

export function arkProvider(): OpenAIProvider {
  assertArkEnabled("ARK text model");
  if (cachedProvider) return cachedProvider;
  cachedProvider = createOpenAI({
    apiKey: arkApiKey(),
    baseURL: arkBaseUrl(),
    name: "byteplus-ark",
  });
  return cachedProvider;
}

export function arkLanguageModel(modelId: string) {
  return arkProvider().chat(resolveArkModelId(modelId));
}

type ArkFetchInit = {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
};

async function arkFetch<T>(path: string, init: ArkFetchInit = {}): Promise<T> {
  assertArkEnabled("ARK request");
  const url = `${arkBaseUrl()}${path.startsWith("/") ? path : `/${path}`}`;
  const response = await fetch(url, {
    method: init.method ?? "POST",
    headers: {
      Authorization: `Bearer ${arkApiKey()}`,
      "Content-Type": "application/json",
    },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
    signal: init.signal,
  });
  const text = await response.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = { raw: text };
  }
  if (!response.ok) {
    const errObj = json as {
      error?: { message?: string; code?: string };
      message?: string;
    } | null;
    const message =
      errObj?.error?.message ||
      errObj?.message ||
      text.slice(0, 400) ||
      `Ark HTTP ${response.status}`;
    throw new Error(`BytePlus Ark ${response.status}: ${message}`);
  }
  return json as T;
}

export type ArkImageResult = {
  data: Uint8Array;
  mediaType: string;
  completionTokens?: number;
};

/**
 * Seedream 5.0 Pro via POST /images/generations.
 * size: "1K" | "2K" | "WxH". 4K Studio tier clamps to 2K.
 */
export async function generateArkImage(args: {
  modelId?: string;
  prompt: string;
  size?: string;
  referenceUrls?: string[];
  watermark?: boolean;
}): Promise<ArkImageResult[]> {
  if (!arkEnabled()) {
    // Seedream has no Higgsfield endpoint; fall back to Studio's GPT Image (OpenRouter).
    const { generateOpenRouterImage } = await import("./openRouterImages");
    const images = await generateOpenRouterImage({
      prompt: args.prompt,
      referenceUrls: args.referenceUrls,
    });
    return images.map((image) => ({ data: image.data, mediaType: image.mediaType || "image/png" }));
  }
  const model = resolveArkModelId(
    args.modelId || process.env.GATEWAY_IMAGE_MODEL_ID || ARK_MODEL_IDS.image,
  );
  const refs = (args.referenceUrls ?? []).map((u) => u.trim()).filter(Boolean);
  const body: Record<string, unknown> = {
    model,
    prompt: args.prompt,
    size: args.size || "2K",
    response_format: "b64_json",
    watermark: args.watermark ?? false,
  };
  if (refs.length === 1) {
    body.image = refs[0];
  } else if (refs.length > 1) {
    body.image = refs.slice(0, 10);
  }

  const result = await arkFetch<{
    data?: Array<{ b64_json?: string; url?: string }>;
  }>("/images/generations", { body });

  const items = result.data ?? [];
  if (!items.length) {
    throw new Error("Seedream returned no images");
  }

  const out: ArkImageResult[] = [];
  for (const item of items) {
    if (item.b64_json) {
      const binary = new Uint8Array(Buffer.from(item.b64_json, "base64"));
      out.push({ data: binary, mediaType: "image/png" });
      continue;
    }
    if (item.url) {
      const media = await downloadMedia(item.url);
      out.push(media);
      continue;
    }
  }
  if (!out.length) {
    throw new Error("Seedream returned empty image payloads");
  }
  return out;
}

export type ArkVideoContentItem =
  | { type: "text"; text: string }
  | {
      type: "image_url";
      image_url: { url: string };
      role?: "first_frame" | "last_frame" | "reference_image";
    }
  | {
      type: "video_url";
      video_url: { url: string };
      role?: "reference_video";
    }
  | {
      type: "audio_url";
      audio_url: { url: string };
      role?: "reference_audio";
    };

export type ArkVideoGenerateInput = {
  modelId?: string;
  prompt: string;
  resolution?: string;
  ratio?: string;
  duration?: number;
  generateAudio?: boolean;
  startFrameUrl?: string;
  /** Locked last frame (first+last-frame I2V). Only sent with a start frame. */
  endFrameUrl?: string;
  referenceImageUrls?: string[];
  referenceVideoUrls?: string[];
  referenceAudioUrls?: string[];
  /** Seedance 2.5 omni_reference_task_type (already resolved; omit for auto). */
  taskType?: "reference" | "edit" | "extend";
  pollTimeoutMs?: number;
  /** Called after each status poll (job heartbeat). */
  onPoll?: () => Promise<void>;
  /** Called once with the BytePlus task id right after create (audit/idempotency). */
  onTaskId?: (taskId: string) => Promise<void> | void;
};

export async function generateArkVideo(
  input: ArkVideoGenerateInput,
): Promise<ArkImageResult> {
  const model = resolveArkModelId(
    input.modelId || process.env.GATEWAY_VIDEO_MODEL_ID || ARK_MODEL_IDS.video25,
  );
  const content: ArkVideoContentItem[] = [
    { type: "text", text: input.prompt },
  ];

  // BytePlus: first-frame I2V, first+last-frame I2V and omni reference
  // (reference_image / reference_video / reference_audio) are mutually
  // exclusive modes. A start frame must stay a locked `first_frame`, so it
  // wins: reference_* entries are not sent alongside it (and it is never
  // demoted into a reference_image, which lets Seedance redraw frame 0).
  const startFrame = input.startFrameUrl?.trim();
  if (startFrame) {
    content.push({
      type: "image_url",
      image_url: { url: startFrame },
      role: "first_frame",
    });
    const endFrame = input.endFrameUrl?.trim();
    if (endFrame) {
      content.push({
        type: "image_url",
        image_url: { url: endFrame },
        role: "last_frame",
      });
    }
    const dropped = {
      images: (input.referenceImageUrls ?? []).filter(
        (url) => url?.trim() && url.trim() !== startFrame,
      ).length,
      videos: (input.referenceVideoUrls ?? []).filter((url) => url?.trim()).length,
      audios: (input.referenceAudioUrls ?? []).filter((url) => url?.trim()).length,
    };
    if (dropped.images || dropped.videos || dropped.audios) {
      console.warn(
        "[ark-seedance] first_frame mode: omitting reference_* inputs (BytePlus forbids mixing)",
        dropped,
      );
    }
  } else {
    for (const url of input.referenceImageUrls ?? []) {
      const trimmed = url?.trim();
      if (!trimmed) continue;
      content.push({
        type: "image_url",
        image_url: { url: trimmed },
        role: "reference_image",
      });
    }
    for (const url of input.referenceVideoUrls ?? []) {
      const trimmed = url?.trim();
      if (!trimmed) continue;
      content.push({
        type: "video_url",
        video_url: { url: trimmed },
        role: "reference_video",
      });
    }
    for (const url of input.referenceAudioUrls ?? []) {
      const trimmed = url?.trim();
      if (!trimmed) continue;
      content.push({
        type: "audio_url",
        audio_url: { url: trimmed },
        role: "reference_audio",
      });
    }
  }

  const body: Record<string, unknown> = {
    model,
    content,
    resolution: input.resolution || "720p",
    duration: input.duration ?? 5,
    generate_audio: input.generateAudio ?? true,
    watermark: false,
  };
  if (startFrame) {
    // First-frame tasks follow the start frame's aspect ratio; Seedance 2.5
    // only accepts `adaptive` here (anything else fails TaskTypeConstraint).
    body.ratio = "adaptive";
  } else {
    const constrained = seedanceTaskTypeConstraints(
      input.taskType,
      input.ratio,
      body.duration as number,
    );
    if (constrained.ratio) body.ratio = constrained.ratio;
    body.duration = constrained.duration;
    if (input.taskType) body.omni_reference_task_type = input.taskType;
  }

  console.info("[ark-seedance] create task", {
    model,
    resolution: body.resolution,
    duration: body.duration,
    ratio: body.ratio ?? null,
    taskType: body.omni_reference_task_type ?? "auto",
    contentTypes: content.map((c) => c.type),
    roles: content.map((c) => ("role" in c ? c.role : undefined) ?? c.type),
  });

  const created = await arkFetch<{ id?: string; task_id?: string }>(
    "/contents/generations/tasks",
    { body },
  );
  const taskId = created.id || created.task_id;
  if (!taskId) {
    throw new Error("Seedance did not return a task id");
  }
  await input.onTaskId?.(taskId);

  const timeoutMs = input.pollTimeoutMs ?? 540_000;
  const started = Date.now();
  let delayMs = 2_500;

  while (Date.now() - started < timeoutMs) {
    await sleep(delayMs);
    delayMs = Math.min(8_000, Math.round(delayMs * 1.25));

    const status = await arkFetch<ArkVideoTaskStatus>(
      `/contents/generations/tasks/${encodeURIComponent(taskId)}`,
      { method: "GET" },
    );
    await input.onPoll?.();

    const state = String(
      status.status || status.task_status || "",
    ).toLowerCase();
    if (
      state === "succeeded" ||
      state === "success" ||
      state === "completed" ||
      state === "done"
    ) {
      const videoUrl = extractVideoUrl(status);
      if (!videoUrl) {
        throw new Error("Seedance succeeded but returned no video URL");
      }
      const media = await downloadMedia(videoUrl);
      const completionTokens = completionTokensFromStatus(status);
      return completionTokens
        ? { ...media, completionTokens }
        : media;
    }
    if (
      state === "failed" ||
      state === "error" ||
      state === "cancelled" ||
      state === "canceled"
    ) {
      const reason = extractArkFailReason(status);
      // Keep the full provider status (minus content urls) on the error so callers can store it.
      const { content: _c, ...detail } = status as unknown as Record<string, unknown>;
      throw Object.assign(new Error(`Seedance task failed: ${reason}`), { arkStatus: detail, arkTaskId: taskId });
    }
  }

  throw new Error(
    `Seedance timed out after ${Math.round(timeoutMs / 1000)}s (task ${taskId})`,
  );
}

type ArkVideoTaskStatus = {
  id?: string;
  status?: string;
  task_status?: string;
  usage?: { completion_tokens?: number; total_tokens?: number };
  message?: string;
  fail_reason?: string;
  error?:
    | string
    | {
        message?: string;
        code?: string;
        type?: string;
        detail?: string;
        details?: unknown;
      };
  content?: {
    video_url?: string;
    file_url?: string;
  };
  output?: {
    video_url?: string;
    url?: string;
    error?: string | { message?: string };
  };
  result?: {
    video_url?: string;
    url?: string;
    error?: string | { message?: string };
  };
  data?: {
    video_url?: string;
    url?: string;
    error?: string | { message?: string };
    fail_reason?: string;
  };
};

/** Pull a short readable reason from nested Ark/Seedance fail payloads. */
export function extractArkFailReason(status: unknown): string {
  if (status == null) return "unknown";
  if (typeof status === "string") {
    const t = status.trim();
    return t || "unknown";
  }
  if (typeof status !== "object") return String(status);

  const s = status as ArkVideoTaskStatus & Record<string, unknown>;
  const parts: string[] = [];

  const push = (value: unknown) => {
    if (value == null) return;
    if (typeof value === "string") {
      const t = value.trim();
      if (t && t !== "undefined" && t !== "[object Object]") parts.push(t);
      return;
    }
    if (typeof value === "number" || typeof value === "boolean") {
      parts.push(String(value));
      return;
    }
    if (typeof value === "object") {
      const o = value as Record<string, unknown>;
      push(o.message);
      push(o.code);
      push(o.type);
      push(o.detail);
      push(o.fail_reason);
      if (o.details != null && typeof o.details !== "object") push(o.details);
      else if (Array.isArray(o.details)) {
        for (const d of o.details.slice(0, 3)) push(d);
      }
    }
  };

  push(s.error);
  push(s.message);
  push(s.fail_reason);
  push(s.output?.error);
  push(s.result?.error);
  push(s.data?.error);
  push(s.data?.fail_reason);
  push(typeof s.status === "string" ? s.status : undefined);
  push(typeof s.task_status === "string" ? s.task_status : undefined);

  if (!parts.length) {
    try {
      const raw = JSON.stringify(status);
      if (raw && raw !== "{}") return raw.slice(0, 500);
    } catch {
      /* ignore */
    }
    return "unknown";
  }

  // Dedupe while preserving order
  const seen = new Set<string>();
  const uniq: string[] = [];
  for (const p of parts) {
    const key = p.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    uniq.push(p);
  }
  return uniq.join(" — ").slice(0, 500);
}

function extractVideoUrl(status: ArkVideoTaskStatus): string | undefined {
  return (
    status.content?.video_url ||
    status.content?.file_url ||
    status.output?.video_url ||
    status.output?.url ||
    status.result?.video_url ||
    status.result?.url ||
    status.data?.video_url ||
    status.data?.url ||
    undefined
  );
}

function completionTokensFromStatus(status: ArkVideoTaskStatus): number | undefined {
  // Output tokens only. total_tokens also counts input (reference video)
  // tokens, so it must never stand in for the billed output count; without
  // completion_tokens the job simply settles at its quote.
  const raw = status.usage?.completion_tokens;
  return typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? raw : undefined;
}

async function downloadMedia(url: string): Promise<ArkImageResult> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download Ark media (${response.status})`);
  }
  const buffer = new Uint8Array(await response.arrayBuffer());
  const mediaType =
    response.headers.get("content-type")?.split(";")[0]?.trim() ||
    guessMediaType(url);
  return { data: buffer, mediaType };
}

function guessMediaType(url: string): string {
  const lower = url.toLowerCase();
  if (lower.includes(".png")) return "image/png";
  if (lower.includes(".jpg") || lower.includes(".jpeg")) return "image/jpeg";
  if (lower.includes(".webp")) return "image/webp";
  if (lower.includes(".webm")) return "video/webm";
  return "video/mp4";
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Map Studio 1K/2K/4K (+ aspect) to Seedream size. 4K clamps to 2K. */
export function seedreamSizeForRequest(
  resolution: string | undefined,
  aspectRatio: string | undefined,
): string {
  const tier = (resolution ?? "2K").toUpperCase();
  // Seedream accepts 1K / 2K labels; pixel WxH also works.
  if (tier === "1K") {
    return seedreamPixelSize("1K", aspectRatio);
  }
  // 2K and clamped 4K
  return seedreamPixelSize("2K", aspectRatio);
}

function seedreamPixelSize(
  tier: "1K" | "2K",
  aspectRatio: string | undefined,
): string {
  const match = aspectRatio?.match(/^(\d+)\s*:\s*(\d+)$/);
  const rw = match ? Number(match[1]) : 1;
  const rh = match ? Number(match[2]) : 1;
  const longEdge = tier === "1K" ? 1536 : 2048;
  if (!rw || !rh || rw === rh) {
    return tier === "1K" ? "1536x1536" : "2048x2048";
  }
  let width: number;
  let height: number;
  if (rw > rh) {
    width = longEdge;
    height = Math.round((longEdge * rh) / rw);
  } else {
    height = longEdge;
    width = Math.round((longEdge * rw) / rh);
  }
  // Keep under 2.36MP for 1K billing when possible.
  if (tier === "1K") {
    const maxPixels = 2_360_000;
    while (width * height > maxPixels) {
      width = Math.max(16, Math.round(width * 0.98));
      height = Math.max(16, Math.round(height * 0.98));
    }
  }
  // Round to even
  width = width - (width % 2);
  height = height - (height % 2);
  return `${width}x${height}`;
}

export function formatArkError(error: unknown): string {
  if (error instanceof Error) {
    return error.message.slice(0, 400);
  }
  return "BytePlus Ark request failed";
}

/** Read one Seedance task (status + error/usage only; no secrets). Used to diagnose failed Ad Engine videos. */
export async function getArkVideoTask(taskId: string): Promise<Record<string, unknown>> {
  const st = await arkFetch<Record<string, unknown>>(`/contents/generations/tasks/${encodeURIComponent(taskId)}`, { method: "GET" });
  const { content: _c, ...rest } = st as Record<string, unknown>;
  return rest;
}

/** List recent Seedance tasks (status/error only). Diagnostics. */
export async function listArkVideoTasks(n = 5): Promise<unknown> {
  const r = await arkFetch<{ items?: Array<Record<string, unknown>> }>(`/contents/generations/tasks?page_num=1&page_size=${n}`, { method: "GET" });
  return (r.items ?? []).map(({ content: _c, ...x }) => x);
}

/** One status check for an existing Seedance task; downloads the video when it has succeeded. */
export async function collectArkVideo(taskId: string): Promise<{ state: "running" | "done" | "failed"; media?: ArkImageResult; tokens?: number; detail?: Record<string, unknown> }> {
  const status = await arkFetch<ArkVideoTaskStatus>(`/contents/generations/tasks/${encodeURIComponent(taskId)}`, { method: "GET" });
  const state = String(status.status || status.task_status || "").toLowerCase();
  const { content: _c, ...detail } = status as unknown as Record<string, unknown>;
  if (["succeeded", "success", "completed", "done"].includes(state)) {
    const url = extractVideoUrl(status); if (!url) return { state: "failed", detail };
    return { state: "done", media: await downloadMedia(url), tokens: completionTokensFromStatus(status), detail };
  }
  if (["failed", "error", "cancelled", "canceled"].includes(state)) return { state: "failed", detail };
  return { state: "running" };
}
